import React, { useState } from "react";
import * as Mui from "@mui/material";
import moment from "moment";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { DescriptionOutlined, ErrorOutlineOutlined, InfoOutlined, ListAltOutlined, PriorityHighRounded, ReplayRounded, WarningAmberRounded } from "@mui/icons-material";
import { showToast } from "services/utils/Status";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { RequestStatusChip } from "components/DesignSystem/RequestStatusChip";
import { RequestDetailsSection } from "components/DesignSystem/RequestDetailsSection";
import { detailAccentPalette } from "components/DesignSystem/RequestDetailField";
import { PrimaryButton, SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import { designColors, designType, designWeights } from "services/constants/designTokens";
import { lwfValidationHeader } from "pages/PayRoll/PayRollHeaders";
import { panelBodySx } from "pages/PayRoll/PayInputs/Shared/formUi";
import { RecordHeaderCard, StatCardGrid, StatutoryList, ToolbarAddButton, initialsOf } from "pages/PayRoll/Shared/statutoryUi";
import { EMPLOYEE_PROFILE_ROUTE } from "pages/PayRoll/Statutory/LWF/LwfEmployeePanel";
import { SEVERITY_COLORS } from "./lwfReportsData";

// Validation (LWF6) and the Validation Issue drawer (LWF6a). Checks run by
// themselves when payroll is finalised and when the LWF set-up, work
// locations, categories or payments change, and again with Re-check now.
// An LWF Blocking issue never stops payroll: only LWF for those employees.

const ALL = "all";
const { pink, blue, green } = detailAccentPalette;
const CONFIG_ROUTE = "/admin/payroll/configuration/statutory-configuration?item=lwf";

const IssuePanel = ({ open, issue, onClose, goTo }) => {
  const history = useHistory();
  if (!issue) return null;
  const action = {
    SITE_NOT_SET_UP: { label: "Set up site", run: () => history.push(CONFIG_ROUTE) },
    CATEGORY_MISSING: { label: "Open employee profile", run: () => history.push(EMPLOYEE_PROFILE_ROUTE) },
    LOW_PAY: { label: "Open LWF Register", run: () => goTo("register") },
    PAYMENT_OVERDUE: { label: "Open Payments", run: () => goTo("payments") },
  }[issue.rule];
  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      title="Validation Issue"
      subtitle={`${issue.severity} · ${issue.site}`}
      bodySx={panelBodySx}
      footer={
        <Mui.Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ width: "100%" }}>
          <SecondaryButton onClick={onClose}>Close</SecondaryButton>
          {action ? (
            <PrimaryButton
              onClick={() => {
                onClose();
                action.run();
              }}
            >
              {action.label}
            </PrimaryButton>
          ) : null}
        </Mui.Stack>
      }
    >
      <Mui.Stack spacing={2}>
        <RecordHeaderCard
          avatar={initialsOf(issue.title)}
          title={issue.title}
          chip={<RequestStatusChip status={issue.severity} colorCode={SEVERITY_COLORS[issue.severity]} />}
          meta={issue.meta}
          tone={issue.severity === "Blocking" ? "danger" : issue.severity === "Warning" ? "warning" : "teal"}
        />
        <RequestDetailsSection
          title="ISSUE"
          fields={[
            { icon: PriorityHighRounded, label: "Problem", value: issue.problem, accent: pink },
            { icon: DescriptionOutlined, label: "Effect", value: issue.effect, helper: issue.effect_note, accent: blue, multiline: true },
          ]}
        />
        {issue.steps.length ? (
          <RequestDetailsSection
            title="HOW TO FIX"
            fields={issue.steps.map(([title, detail], index) => ({
              icon: () => (
                <Mui.Box component="span" sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1 }}>
                  {index + 1}
                </Mui.Box>
              ),
              label: title,
              value: detail,
              accent: green,
              multiline: true,
            }))}
          />
        ) : (
          <RequestInfoBanner body="Nothing to fix — this is for your information." />
        )}
      </Mui.Stack>
    </GlobalSidePanel>
  );
};

export const LwfValidationTab = ({ issues, lastCheck, onRecheck, goTo }) => {
  const { domain, userDetails, topbarCompanyName } = useSelector((state) => state.tokenReducer);
  const [card, setCard] = useState(ALL);
  const [viewId, setViewId] = useState(null);
  const { visibleHeaders, titleOverride } = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    storageKey: "lwf_validation_saved_views",
    fullHeaders: lwfValidationHeader,
  });

  const rows = card === ALL ? issues : issues.filter((item) => item.severity === card);
  const count = (severity) => issues.filter((item) => item.severity === severity).length;

  const renderCell = (row, key) => {
    switch (key) {
      case "name":
        return row.kind === "employee" ? (
          <Mui.Stack direction="row" spacing={1} alignItems="center">
            <Mui.Avatar sx={{ width: 28, height: 28, fontSize: 11, fontWeight: 700, backgroundColor: designColors.slate100, color: designColors.slate700 }}>{initialsOf(row.name)}</Mui.Avatar>
            <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: designWeights.medium, whiteSpace: "nowrap" }}>{row.name}</Mui.Typography>
          </Mui.Stack>
        ) : (
          <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: designWeights.semibold, whiteSpace: "nowrap" }}>{row.name}</Mui.Typography>
        );
      case "severity":
        return <RequestStatusChip status={row.severity} colorCode={SEVERITY_COLORS[row.severity]} />;
      default:
        return row[key] || "—";
    }
  };

  return (
    <>
      <StatCardGrid
        value={card}
        onChange={setCard}
        cards={[
          { key: ALL, title: "All issues", value: issues.length, icon: ListAltOutlined, tone: "blue" },
          { key: "Blocking", title: "Blocking", value: count("Blocking"), icon: ErrorOutlineOutlined, tone: "red" },
          { key: "Warning", title: "Warning", value: count("Warning"), icon: WarningAmberRounded, tone: "amber" },
          { key: "Info", title: "Info", value: count("Info"), icon: InfoOutlined, tone: "green" },
        ]}
      />
      <Mui.Typography sx={{ ...designType.captionText, mb: 1 }}>
        Last checked {moment(lastCheck.at).format("DD MMM YYYY, HH:mm")} · trigger: {lastCheck.trigger}
      </Mui.Typography>
      <StatutoryList
        title="LWF Validation"
        headers={visibleHeaders}
        titleOverride={titleOverride}
        rows={rows}
        renderCell={renderCell}
        onRowClick={(row) => setViewId(row.id)}
        selectedKey={viewId}
        itemLabel="issues"
        searchPlaceholder="Search"
        metadata={{ "Company Name": topbarCompanyName || "N/A", "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss") }}
        action={
          <ToolbarAddButton
            startIcon={<ReplayRounded />}
            onClick={() => {
              onRecheck();
              showToast("success", "Validation checked again.");
            }}
          >
            Re-check now
          </ToolbarAddButton>
        }
      />
      <Mui.Box sx={{ mt: 1.5 }}>
        <RequestInfoBanner body="Checks run by themselves when payroll is finalised and when LWF set-up, work locations, categories or payments change. Blocking means LWF could not be worked out for those employees — the rest of payroll still finalises." />
      </Mui.Box>
      <IssuePanel open={Boolean(viewId)} issue={issues.find((item) => item.id === viewId)} onClose={() => setViewId(null)} goTo={goTo} />
    </>
  );
};
